import React from "react";
import { Search, SlidersHorizontal } from "lucide-react";

const inputCls =
  "bg-white border border-[#D2E0E6] text-[#0B2830] mono text-sm px-3 py-2 rounded-md focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none w-full";
const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5";

export default function FilterBar({
  filters, setFilters, sectors, search, setSearch,
  qualifiedOnly, setQualifiedOnly, period, setPeriod, onApply, dirty,
}) {
  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const num = (k, label, testid) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={testid} type="number" value={filters[k]}
        onChange={(e) => upd(k, e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onApply()} className={inputCls} />
    </div>
  );

  return (
    <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-4 fade-up" data-testid="filter-bar">
      {/* Basis toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#0284C7]" />
          <span className="mono text-xs uppercase tracking-[0.2em] text-[#47616B]">Screen filters</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.15em] text-[#7C949C]">Basis</span>
          <div className="inline-flex bg-[#E9F1F4] border border-[#D2E0E6] rounded-md p-0.5" data-testid="period-toggle">
            {["quarterly", "annual"].map((p) => (
              <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs mono rounded transition-colors ${
                  period === p ? "bg-[#0284C7] text-white" : "text-[#47616B] hover:text-[#0B2830]"
                }`}>
                {p === "quarterly" ? "Quarterly" : "Annual"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div>
          <label className={labelCls}>Sector</label>
          <select data-testid="filter-sector" value={filters.sector}
            onChange={(e) => upd("sector", e.target.value)} className={inputCls}>
            <option value="">All sectors</option>
            {sectors.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        {num("min_mcap_b", "Min Mkt Cap ($B)", "filter-min-mcap")}
        {num("max_mcap_b", "Max Mkt Cap ($B)", "filter-max-mcap")}
        {num("max_pe", "Max P/E", "filter-max-pe")}
        {num("min_rev_growth", "Min Rev Growth %", "filter-min-rev-growth")}
        {num("min_op_margin", "Min Operating Margin %", "filter-min-op-margin")}
        {num("min_net_margin", "Min Net Profit Margin %", "filter-min-net-margin")}
        {num("min_ebitda_margin", "Min EBITDA Margin %", "filter-min-ebitda-margin")}
        {num("max_pe_vs_peer", "Max P/E \u00F7 Peer", "filter-max-pe-vs-peer")}
        {num("max_payout", "Max Payout %", "filter-max-payout")}
        {num("min_annual_growth", "Min Annual EBITDA Growth %", "filter-min-annual-growth")}
        {num("max_per_sector", "Max Names / Sector", "filter-max-per-sector")}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-[#E6EEF1]">
        <button data-testid="apply-filters-btn" onClick={onApply}
          className={`px-4 py-2 text-sm mono rounded-md transition-colors ${
            dirty ? "bg-[#0284C7] text-white hover:bg-[#0369A1]" : "bg-[#0284C7] text-white hover:bg-[#0369A1]"
          }`}>
          Apply Filters{dirty ? " \u2022" : ""}
        </button>
        <button data-testid="filter-require-quarterly-toggle"
          onClick={() => upd("require_quarterly_growth", !filters.require_quarterly_growth)}
          className={`px-3 py-1.5 text-xs mono border rounded-md transition-colors ${
            filters.require_quarterly_growth ? "bg-[#0D9488] border-[#0D9488] text-white"
              : "bg-transparent border-[#D2E0E6] text-[#47616B] hover:bg-[#E0EBEF]"
          }`}>
          {filters.require_quarterly_growth ? "\u25A0" : "\u25A1"} Require +ve quarterly EBITDA
        </button>
        <button data-testid="filter-qualified-toggle" onClick={() => setQualifiedOnly((v) => !v)}
          className={`px-3 py-1.5 text-xs mono border rounded-md transition-colors ${
            qualifiedOnly ? "bg-[#0D9488] border-[#0D9488] text-white"
              : "bg-transparent border-[#D2E0E6] text-[#47616B] hover:bg-[#E0EBEF]"
          }`}>
          {qualifiedOnly ? "\u25A0" : "\u25A1"} Fully-qualified only
        </button>
        <div className="relative ml-auto min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-[#7C949C]" />
          <input data-testid="filter-search" value={search} placeholder="Ticker / name"
            onChange={(e) => setSearch(e.target.value)} className={inputCls + " pl-8"} />
        </div>
      </div>
    </div>
  );
}
